import type { NervousSystem } from '../pharmacology/types';
import { requireElement } from './domHelpers';

const SYSTEM_ORDER: NervousSystem[] = ['central', 'somatic', 'autonomic', 'enteric'];

const SYSTEM_LABELS: Record<NervousSystem, string> = {
  central: 'Central',
  somatic: 'Somatic',
  autonomic: 'Autonomic',
  enteric: 'Enteric',
};

const SYSTEM_HINTS: Record<NervousSystem, string> = {
  central: 'Brain and spinal cord',
  somatic: 'Voluntary movement and sensation',
  autonomic: 'Heart rate, blood vessels, glands',
  enteric: 'The gut’s own nervous system',
};

const SYSTEM_COLORS: Record<NervousSystem, string> = {
  central: '#37d6ff',
  somatic: '#53e0c4',
  autonomic: '#ff9f5a',
  enteric: '#c98bff',
};

/**
 * Bar readout of how strongly each nervous-system subdivision is engaged.
 *
 * This is where "the whole nervous system" becomes legible rather than merely
 * visible: it makes the difference between, say, lidocaine (almost entirely
 * peripheral) and an SSRI (almost entirely central) immediately readable
 * instead of something you have to infer by hunting for lit markers.
 */
export class SystemActivityPanel {
  private readonly bars = new Map<NervousSystem, { fill: HTMLElement; value: HTMLElement }>();

  constructor() {
    const container = requireElement<HTMLElement>('system-activity');
    container.innerHTML = '';

    for (const system of SYSTEM_ORDER) {
      const row = document.createElement('div');
      row.className = 'system-row';
      row.title = SYSTEM_HINTS[system];

      const label = document.createElement('span');
      label.className = 'system-label';
      label.textContent = SYSTEM_LABELS[system];

      const track = document.createElement('span');
      track.className = 'system-track';

      const fill = document.createElement('span');
      fill.className = 'system-fill';
      fill.style.background = SYSTEM_COLORS[system];
      track.appendChild(fill);

      const value = document.createElement('span');
      value.className = 'system-value';
      value.textContent = '—';

      row.append(label, track, value);
      container.appendChild(row);
      this.bars.set(system, { fill, value });
    }
  }

  update(activity: Record<NervousSystem, number> | null): void {
    for (const system of SYSTEM_ORDER) {
      const bar = this.bars.get(system)!;
      const level = activity?.[system] ?? 0;
      bar.fill.style.width = `${Math.round(level * 100)}%`;
      // Below 0.5% the readout is noise, so show a dash rather than "0%".
      bar.value.textContent = activity && level >= 0.005 ? `${Math.round(level * 100)}%` : '—';
    }
  }
}
